import React, { useEffect, useState } from 'react';
import { UserRequest } from '../types';
import { FileText, User, Calendar } from 'lucide-react';

interface RequestAnimationCardProps {
  isVisible: boolean;
  request: UserRequest | null;
  onAnimationComplete: () => void;
  onFormShrinkStart?: () => void;
}

export const RequestAnimationCard: React.FC<RequestAnimationCardProps> = ({
  isVisible,
  request,
  onAnimationComplete,
  onFormShrinkStart
}) => {
  const [animationPhase, setAnimationPhase] = useState<'hidden' | 'form-shrink' | 'card-appear' | 'card-fly' | 'complete'>('hidden');

  useEffect(() => {
    if (!isVisible || !request) {
      setAnimationPhase('hidden');
      return;
    }

    // Phase 1: Start with form shrinking (0.15s)
    setAnimationPhase('form-shrink');
    if (onFormShrinkStart) {
      onFormShrinkStart();
    }
    
    // Phase 2: After form shrinks, show the card and start flying immediately (0.15s)
    const cardAppearTimer = setTimeout(() => {
      setAnimationPhase('card-appear');
    }, 150);

    // Phase 3: Start the card flying animation immediately after appearing (0.15s delay, then 0.4s duration)
    const cardFlyTimer = setTimeout(() => {
      setAnimationPhase('card-fly');
    }, 150);

    // Phase 4: Complete the animation (0.55s total)
    const completeTimer = setTimeout(() => {
      setAnimationPhase('complete');
      onAnimationComplete();
    }, 550);

    return () => {
      clearTimeout(cardAppearTimer);
      clearTimeout(cardFlyTimer);
      clearTimeout(completeTimer);
    };
  }, [isVisible, request, onAnimationComplete]);

  if (!isVisible || !request || animationPhase === 'hidden') {
    return null;
  }

  const getTransformClasses = () => {
    switch (animationPhase) {
      case 'form-shrink':
        return 'scale-0 translate-x-0 translate-y-0 opacity-0'; // Hidden during form shrink
      case 'card-appear':
        return 'scale-100 translate-x-0 translate-y-0 opacity-100'; // Appear at full size
      case 'card-fly':
        return 'scale-75 -translate-x-96 -translate-y-24 opacity-90'; // Shrink less and fly more smoothly
      case 'complete':
        return 'scale-50 -translate-x-96 -translate-y-24 opacity-0'; // Fade out while maintaining position
      default:
        return 'scale-0 translate-x-0 translate-y-0 opacity-0';
    }
  };

  return (
    <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
      <div
        className={`
          bg-white rounded-lg shadow-xl border border-gray-200 p-6 max-w-md w-full mx-4
          transition-all duration-400 ease-in-out transform-gpu
          ${getTransformClasses()}
        `}
      >
        {/* Request Card Content */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-900">New Visit</span>
            </div>
            <div className="text-xs text-gray-500">
              {new Date(request.createdAt).toLocaleTimeString()}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              <span>{request.patientName || 'No Patient Selected'}</span>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{new Date(request.createdAt).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">Task</div>
            <div className="text-sm text-gray-900 mt-1">{request.taskName || 'Document Generation'}</div>
          </div>

          <div className="flex justify-between items-center">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Queued
            </span>
            <span className="text-xs text-gray-500">Processing...</span>
          </div>
        </div>
      </div>
    </div>
  );
};